import {
    Button,
    Divider,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    makeStyles,
    Text,
    tokens,
    Tooltip,
} from '@fluentui/react-components';
import { CopyRegular, DismissRegular, OpenRegular } from '@fluentui/react-icons';
import { format, parseISO } from 'date-fns';
import { ClientListingResponse } from '../../api';
import ClientAvatar from './ClientAvatar';
import ClientTypeBadge from './ClientTypeBadge';
import CreatedByPersona from './CreatedByPersona';

const useStyles = makeStyles({
    drawer: {
        width: '440px',
        maxWidth: '92vw',
    },
    headerTop: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px',
        width: '100%',
    },
    headerMeta: {
        minWidth: 0,
        flex: 1,
    },
    name: {
        fontSize: '18px',
        fontWeight: tokens.fontWeightBold,
        lineHeight: 1.25,
        display: 'block',
    },
    badgeRow: {
        marginTop: '6px',
    },
    sectionLabel: {
        fontSize: '11px',
        fontWeight: tokens.fontWeightBold,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: tokens.colorNeutralForeground3,
        margin: '20px 0 10px',
    },
    fieldGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '14px 18px',
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
        minWidth: 0,
    },
    fieldFull: {
        gridColumn: '1 / -1',
    },
    fLabel: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        fontWeight: tokens.fontWeightSemibold,
    },
    fValue: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
        fontWeight: tokens.fontWeightMedium,
    },
    mono: {
        fontFamily: tokens.fontFamilyMonospace,
        fontSize: '13px',
        fontVariantNumeric: 'tabular-nums',
    },
    footer: {
        display: 'flex',
        gap: '10px',
        padding: '16px 24px',
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    footerButton: {
        flex: 1,
    },
});

const formatTimestamp = (iso: string | undefined): string => {
    if (!iso) return '--';
    try {
        return format(parseISO(iso), "MMM d, yyyy 'at' h:mm a");
    } catch {
        return iso;
    }
};

interface ClientDetailDrawerProps {
    client: ClientListingResponse | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onCopyId?: (clientId: string) => void;
}

const Field = ({ label, value, full, mono, className }: {
    label: string;
    value: React.ReactNode;
    full?: boolean;
    mono?: boolean;
    className?: string;
}) => {
    const styles = useStyles();
    return (
        <div className={full ? `${styles.field} ${styles.fieldFull}` : styles.field}>
            <span className={styles.fLabel}>{label}</span>
            <span className={mono ? `${styles.fValue} ${styles.mono}` : styles.fValue}>{value}</span>
        </div>
    );
};

/**
 * Right slide-over showing client detail. Only fields available from the
 * listing response are shown (EIN / fiscal year end / status require a future
 * detail endpoint — see the plan). Copy ID is functional; Open in CCH is a
 * stub pending a deep-link URL.
 */
const ClientDetailDrawer = ({ client, open, onOpenChange, onCopyId }: ClientDetailDrawerProps) => {
    const styles = useStyles();

    const handleCopy = () => {
        if (!client) return;
        void navigator.clipboard?.writeText(client.clientId);
        onCopyId?.(client.clientId);
    };

    return (
        <Drawer
            className={styles.drawer}
            type="overlay"
            position="end"
            open={open}
            onOpenChange={(_, data) => onOpenChange(data.open)}
        >
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Button
                            appearance="subtle"
                            aria-label="Close"
                            icon={<DismissRegular />}
                            onClick={() => onOpenChange(false)}
                        />
                    }
                >
                    {client && (
                        <div className={styles.headerTop}>
                            <ClientAvatar name={client.name} clientType={client.clientType} size="large" />
                            <div className={styles.headerMeta}>
                                <Text className={styles.name}>{client.name}</Text>
                                <div className={styles.badgeRow}>
                                    <ClientTypeBadge clientType={client.clientType} />
                                </div>
                            </div>
                        </div>
                    )}
                </DrawerHeaderTitle>
            </DrawerHeader>

            <DrawerBody>
                {client && (
                    <>
                        <div className={styles.sectionLabel}>Identification</div>
                        <div className={styles.fieldGrid}>
                            <Field label="Client ID" value={client.clientId} mono />
                            <Field label="Client Type" value={<ClientTypeBadge clientType={client.clientType} />} />
                        </div>

                        <div className={styles.sectionLabel}>Engagement</div>
                        <div className={styles.fieldGrid}>
                            <Field label="Office" value={client.officeName || '--'} />
                            <Field label="Business Unit" value={client.businessUnitName || '--'} />
                        </div>

                        <div className={styles.sectionLabel}>Record</div>
                        <div className={styles.fieldGrid}>
                            <Field label="Created By" value={<CreatedByPersona name={client.createdBy} />} full />
                            <Field label="Created At" value={formatTimestamp(client.createdAt)} full />
                        </div>
                    </>
                )}
            </DrawerBody>

            <div className={styles.footer}>
                <Button className={styles.footerButton} icon={<CopyRegular />} onClick={handleCopy}>
                    Copy ID
                </Button>
                <Tooltip content="Coming soon" relationship="label">
                    <Button className={styles.footerButton} appearance="primary" icon={<OpenRegular />} disabled>
                        Open in CCH
                    </Button>
                </Tooltip>
            </div>
        </Drawer>
    );
};

export default ClientDetailDrawer;
